const NODAL_DISTANCE = 0.009
const OUTLINE_FRACTION = 0.34

type Point = readonly [number, number]
type Segment = (t: number) => Point
type Basis = "cos" | "sin"

export interface ChladniMode {
  id: `chladni-${string}`
  label: string
  basis: Basis
  n: number
  m: number
  sign: -1 | 1
  minSize: number
}

const NODAL_CACHE = new Map<string, ReadonlyArray<Point>>()

export function clamp01(value: number) {
  return Math.max(0, Math.min(1, value))
}

export function smoothstep(value: number) {
  const t = clamp01(value)
  return t * t * (3 - 2 * t)
}

export function ellipseSegment(
  rx: number,
  ry: number,
  rotation = 0,
  cx = 0,
  cy = 0,
): Segment {
  const cosRotation = Math.cos(rotation)
  const sinRotation = Math.sin(rotation)
  return (t) => {
    const angle = t * Math.PI * 2
    const x = rx * Math.cos(angle)
    const y = ry * Math.sin(angle)
    return [
      cx + x * cosRotation - y * sinRotation,
      cy + x * sinRotation + y * cosRotation,
    ]
  }
}

function lineSegment(from: Point, to: Point): Segment {
  return (t) => [
    from[0] + (to[0] - from[0]) * t,
    from[1] + (to[1] - from[1]) * t,
  ]
}

function compositeSegment(segments: readonly Segment[]): Segment {
  return (t) => {
    const scaled = Math.min(t, 1 - Number.EPSILON) * segments.length
    const index = Math.floor(scaled)
    return segments[index](scaled - index)
  }
}

export function polylineSegment(
  points: readonly Point[],
  closed = true,
): Segment {
  const segments: Segment[] = []
  const edgeCount = closed ? points.length : points.length - 1
  for (let index = 0; index < edgeCount; index += 1) {
    segments.push(
      lineSegment(points[index], points[(index + 1) % points.length]),
    )
  }
  return compositeSegment(segments)
}

export function superellipseSegment(
  radiusX: number,
  radiusY: number,
  exponent: number,
  rotation = 0,
): Segment {
  const power = 2 / exponent
  const cosRotation = Math.cos(rotation)
  const sinRotation = Math.sin(rotation)
  return (t) => {
    const angle = t * Math.PI * 2
    const cosAngle = Math.cos(angle)
    const sinAngle = Math.sin(angle)
    const x = Math.sign(cosAngle) * Math.abs(cosAngle) ** power * radiusX
    const y = Math.sign(sinAngle) * Math.abs(sinAngle) ** power * radiusY
    return [
      x * cosRotation - y * sinRotation,
      x * sinRotation + y * cosRotation,
    ]
  }
}

function sampleShape(segments: readonly Segment[], count: number) {
  if (segments.length === 0 || count <= 0) {
    return new Float32Array()
  }

  const points = new Float32Array(count * 2)
  const samples = segments.map((segment, segmentIndex) =>
    sampleSegmentByArcLength(
      segment,
      Math.floor(
        (count + segments.length - 1 - segmentIndex) / segments.length,
      ),
    ),
  )
  for (let index = 0; index < count; index += 1) {
    const segmentIndex = index % segments.length
    const step = Math.floor(index / segments.length)
    const segmentPoints = samples[segmentIndex]
    points[index * 2] = segmentPoints[step * 2]
    points[index * 2 + 1] = segmentPoints[step * 2 + 1]
  }
  return points
}

function sampleSegmentByArcLength(segment: Segment, count: number) {
  if (count <= 0) {
    return new Float32Array()
  }

  const resolution = Math.max(192, count * 12)
  const densePoints = new Float32Array((resolution + 1) * 2)
  const cumulativeLength = new Float32Array(resolution + 1)
  let [previousX, previousY] = segment(0)
  densePoints[0] = previousX
  densePoints[1] = previousY
  for (let index = 1; index <= resolution; index += 1) {
    const [x, y] = segment(index / resolution)
    const offset = index * 2
    densePoints[offset] = x
    densePoints[offset + 1] = y
    cumulativeLength[index] =
      cumulativeLength[index - 1] + Math.hypot(x - previousX, y - previousY)
    previousX = x
    previousY = y
  }

  const points = new Float32Array(count * 2)
  const totalLength = cumulativeLength[resolution]
  let denseIndex = 0
  for (let index = 0; index < count; index += 1) {
    const targetLength = (index / count) * totalLength
    while (
      denseIndex < resolution - 1 &&
      cumulativeLength[denseIndex + 1] < targetLength
    ) {
      denseIndex += 1
    }
    const segmentLength =
      cumulativeLength[denseIndex + 1] - cumulativeLength[denseIndex] || 1
    const progress =
      (targetLength - cumulativeLength[denseIndex]) / segmentLength
    const denseOffset = denseIndex * 2
    points[index * 2] =
      densePoints[denseOffset] +
      (densePoints[denseOffset + 2] - densePoints[denseOffset]) * progress
    points[index * 2 + 1] =
      densePoints[denseOffset + 1] +
      (densePoints[denseOffset + 3] - densePoints[denseOffset + 1]) * progress
  }
  return points
}

function sortPointsSpatially(points: Float32Array) {
  const order = Array.from({ length: points.length / 2 }, (_, index) => index)
  order.sort((first, second) => {
    const firstX = points[first * 2]
    const firstY = points[first * 2 + 1]
    const secondX = points[second * 2]
    const secondY = points[second * 2 + 1]
    const angleDifference =
      Math.atan2(firstY, firstX) - Math.atan2(secondY, secondX)
    return (
      angleDifference ||
      Math.hypot(firstX, firstY) - Math.hypot(secondX, secondY)
    )
  })

  const sorted = new Float32Array(points.length)
  order.forEach((sourceIndex, targetIndex) => {
    sorted[targetIndex * 2] = points[sourceIndex * 2]
    sorted[targetIndex * 2 + 1] = points[sourceIndex * 2 + 1]
  })
  return sorted
}

function makeRandom(seed: number) {
  let state = seed || 1
  return () => {
    state ^= state << 13
    state ^= state >>> 17
    state ^= state << 5
    return (state >>> 0) / 0x100000000
  }
}

function seedFromId(id: string) {
  let hash = 2166136261
  for (let index = 0; index < id.length; index += 1) {
    hash ^= id.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function fieldValue(x: number, y: number, mode: ChladniMode) {
  const basis = mode.basis === "cos" ? Math.cos : Math.sin
  const first = basis(mode.m * Math.PI * x) * basis(mode.n * Math.PI * y)
  const second = basis(mode.n * Math.PI * x) * basis(mode.m * Math.PI * y)
  return first + mode.sign * second
}

function scanNodalLine(mode: ChladniMode) {
  const cached = NODAL_CACHE.get(mode.id)
  if (cached) {
    return cached
  }

  const resolution = Math.max(72, Math.max(mode.n, mode.m) * 10)
  const epsilon = 1e-3
  const points: Point[] = []
  for (let xIndex = 0; xIndex <= resolution; xIndex += 1) {
    const x = xIndex / resolution
    for (let yIndex = 0; yIndex <= resolution; yIndex += 1) {
      const y = yIndex / resolution
      const value = fieldValue(x, y, mode)
      const gradientX =
        (fieldValue(x + epsilon, y, mode) - fieldValue(x - epsilon, y, mode)) /
        (2 * epsilon)
      const gradientY =
        (fieldValue(x, y + epsilon, mode) - fieldValue(x, y - epsilon, mode)) /
        (2 * epsilon)
      if (
        Math.abs(value) / (Math.hypot(gradientX, gradientY) + 1e-6) <
        NODAL_DISTANCE
      ) {
        points.push([x * 2 - 1, y * 2 - 1])
      }
    }
  }
  NODAL_CACHE.set(mode.id, points)
  return points
}

export function selectNodalTargets(
  mode: ChladniMode,
  count: number,
  scale: number,
) {
  const candidates = scanNodalLine(mode)
  const result = new Float32Array(count * 2)
  if (candidates.length === 0) {
    return result
  }

  const random = makeRandom(seedFromId(mode.id))
  const order = candidates.map((_, index) => index)
  for (let index = order.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1))
    ;[order[index], order[swapIndex]] = [order[swapIndex], order[index]]
  }

  const chosen: number[] = []
  const taken = new Uint8Array(candidates.length)
  let minDistance = 0.22
  while (chosen.length < count && minDistance > 0.04) {
    for (const candidateIndex of order) {
      if (chosen.length >= count || taken[candidateIndex]) {
        continue
      }
      const [x, y] = candidates[candidateIndex]
      const isSeparated = chosen.every((chosenIndex) => {
        const [chosenX, chosenY] = candidates[chosenIndex]
        return (
          Math.hypot((chosenX - x) * scale, (chosenY - y) * scale) >=
          minDistance
        )
      })
      if (isSeparated) {
        chosen.push(candidateIndex)
        taken[candidateIndex] = 1
      }
    }
    minDistance *= 0.68
  }
  for (const candidateIndex of order) {
    if (chosen.length >= count) {
      break
    }
    if (!taken[candidateIndex]) {
      chosen.push(candidateIndex)
    }
  }

  for (let index = 0; index < count; index += 1) {
    const candidate = candidates[chosen[index % chosen.length]]
    result[index * 2] = candidate[0] * scale
    result[index * 2 + 1] = candidate[1] * scale
  }
  return result
}

export function buildOutlinedFigure(
  count: number,
  outline: Segment,
  inner:
    | readonly Segment[]
    | Float32Array
    | ((innerCount: number) => Float32Array),
  outlineFraction = OUTLINE_FRACTION,
) {
  const outlineCount = Math.round(count * clamp01(outlineFraction))
  const innerCount = count - outlineCount
  const outlinePoints = sortPointsSpatially(
    sampleSegmentByArcLength(outline, outlineCount),
  )
  const resolvedInner = typeof inner === "function" ? inner(innerCount) : inner
  const innerPoints = sortPointsSpatially(
    resolvedInner instanceof Float32Array
      ? resolvedInner
      : sampleShape(resolvedInner, innerCount),
  )
  const points = new Float32Array(count * 2)
  points.set(outlinePoints)
  points.set(innerPoints, outlinePoints.length)
  return points
}
